import { Component, ErrorInfo, ReactNode } from 'react'

type ErrorBoundaryProps = {
  children: ReactNode
  /** Optional label used in the fallback heading (e.g. the page/section name). */
  label?: string
  /** Remount children when this value changes (e.g. the route key) so navigation clears the error. */
  resetKey?: unknown
}

type ErrorBoundaryState = {
  error: Error | null
}

/**
 * Catches render-time exceptions in its subtree and shows an inline fallback instead of letting
 * the error unmount the whole React app (which previously produced a blank white page and made
 * troubleshooting impossible). react-router's `errorElement` only covers data-router loader
 * errors, not component render errors, so this boundary is needed.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Caught render error:', error, info.componentStack)
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    // Clear the error when the reset key changes (e.g. the user navigates to another route).
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null })
    }
  }

  render(): ReactNode {
    const { error } = this.state
    if (error) {
      return (
        <div className="w-full max-w-3xl mx-auto my-12 p-6 rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700">
          <div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold">
            <i className="pi pi-exclamation-triangle" />
            <span>Something went wrong{this.props.label ? ` in ${this.props.label}` : ''}.</span>
          </div>
          <p className="mt-2 text-sm text-gray-700 dark:text-neutral-20">
            This section failed to render. The rest of the app is still usable — try navigating away
            and back, or reload the page. If it keeps happening, share the details below with the
            CFD team.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              className="text-sm font-medium text-gm-blue underline"
              onClick={() => this.setState({ error: null })}>
              Try again
            </button>
            <button
              type="button"
              className="text-sm font-medium text-gm-blue underline"
              onClick={() => window.location.reload()}>
              Reload page
            </button>
          </div>
          <pre className="mt-3 text-xs whitespace-pre-wrap bg-white/60 dark:bg-black/30 p-2 rounded max-h-48 overflow-auto text-red-800 dark:text-red-300">
            {error.message}
          </pre>
        </div>
      )
    }
    return this.props.children
  }
}

export default ErrorBoundary
